import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authApi } from "../api/auth";
import type { User } from "../api/types";

const ME_KEY = ["auth", "me"];

/**
 * Who is signed in. `undefined` while loading, `null` when signed out.
 *
 * The session lives in a HttpOnly cookie, so this query is the only way the
 * SPA learns about it.
 */
export function useCurrentUser() {
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: authApi.me,
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    user: query.data ?? null,
    isLoading: query.isPending,
  };
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: (user: User) => queryClient.setQueryData(ME_KEY, user),
  });
}

export function useSignup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.signup,
    onSuccess: (user: User) => queryClient.setQueryData(ME_KEY, user),
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.updateAccount,
    onSuccess: (user: User) => queryClient.setQueryData(ME_KEY, user),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({ current, next }: { current: string; next: string }) =>
      authApi.changePassword(current, next),
  });
}

/** Ends the session client-side once the server has ended it. */
function useSignedOut() {
  const queryClient = useQueryClient();
  return () => {
    // Everything cached belongs to the account that just signed out.
    queryClient.clear();
    queryClient.setQueryData(ME_KEY, null);
  };
}

export function useLogout() {
  return useMutation({ mutationFn: authApi.logout, onSuccess: useSignedOut() });
}

export function useLogoutEverywhere() {
  return useMutation({ mutationFn: authApi.logoutEverywhere, onSuccess: useSignedOut() });
}

export function useDeleteAccount() {
  return useMutation({ mutationFn: authApi.deleteAccount, onSuccess: useSignedOut() });
}
